import { useEffect, useRef } from "react";

/**
 * Custom hook to monitor user activity and verify session validity with the backend when idle.
 * If validation fails (e.g. token expired or invalid), it automatically triggers clearSession.
 *
 * @param {string} token - The active auth token
 * @param {object} user - The active user object
 * @param {object} api - The API client context
 * @param {function} clearSession - Function to clear auth session states
 */
export const useIdleTimeout = (token, user, api, clearSession) => {
  const lastActivityTimeRef = useRef(Date.now());
  const isVerifyingRef = useRef(false);

  useEffect(() => {
    if (!token || !user) return;

    // Reset refs when token or user changes (e.g. login/logout)
    lastActivityTimeRef.current = Date.now();
    isVerifyingRef.current = false;

    const handleActivity = () => {
      lastActivityTimeRef.current = Date.now();
    };

    // User activity listeners
    const activityEvents = ["mousemove", "keydown", "click", "scroll"];
    activityEvents.forEach((event) => {
      window.addEventListener(event, handleActivity);
    });

    // Check idle status every 30 seconds
    const intervalId = setInterval(async () => {
      const idleTime = Date.now() - lastActivityTimeRef.current;
      const timeoutLimit = 15 * 60 * 1000; // 15 minutes idle timeout

      if (idleTime >= timeoutLimit && !isVerifyingRef.current) {
        isVerifyingRef.current = true;
        console.log("Session has been idle. Verifying token validity...");
        try {
          await api.get("/auth/me");
          // If valid, reset activity time and continue session
          lastActivityTimeRef.current = Date.now();
          isVerifyingRef.current = false;
        } catch (error) {
          console.error("Idle session verification failed. Logging out:", error);
          clearSession();
        }
      }
    }, 30000); // Check every 30 seconds

    return () => {
      clearInterval(intervalId);
      activityEvents.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
    };
  }, [token, user, api, clearSession]);
};
